import { useState } from "react";
import { useTranslation } from "react-i18next";
import { apiDownload, type ApiError } from "../api/client";
import { useToast } from "./Toast";
import Ic from "./icons.jsx";

type Props = {
  /** API path with the list's current filters, e.g. `/exports/assets.xlsx?q=…` (no `/api` prefix). */
  path: string;
  label: string;
  /** Used when the server sends no file name of its own. */
  filename: string;
  /** Structured criteria for exports that take them in the body (sent as POST). */
  body?: unknown;
  className?: string;
};

/**
 * "Izvezi u Excel": asks the server for the file and hands it to the browser as
 * a download (blueprint 04 §ExportButton). The file is built per request, so the
 * button stays disabled — same label, no layout shift — until it arrives.
 */
export function ExportButton({ path, label, filename, body, className = "btn lg" }: Props) {
  const { t } = useTranslation();
  const toast = useToast();
  const [busy, setBusy] = useState(false);

  async function run() {
    if (busy) return;
    setBusy(true);
    try {
      const file = await apiDownload(path, body);
      const url = URL.createObjectURL(file.blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = file.filename ?? filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      // Safari starts the download asynchronously; revoking right away can cancel it.
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (err) {
      const e = err as ApiError;
      toast.error(
        e.code === "EXPORT_TOO_LARGE"
          ? t("export.tooLarge", "Previše redova za jedan izvoz. Suzite filtere pa pokušajte ponovo.")
          : e.code === "FORBIDDEN"
            ? e.message
            : t("export.failed", "Izvoz nije uspeo. Pokušajte ponovo."),
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <button type="button" className={className} onClick={run} disabled={busy} aria-busy={busy}>
      <Ic.download /> {label}
    </button>
  );
}
